package xim.poc.game.configuration.v0.tactics

import xim.poc.ModelLook
import xim.poc.NoOpActorController
import xim.poc.audio.AudioManager
import xim.poc.audio.BackgroundMusicResponse
import xim.poc.audio.SystemSound
import xim.poc.game.*
import xim.poc.game.actor.components.Inventory
import xim.poc.game.actor.components.getInventory
import xim.poc.game.configuration.*
import xim.poc.game.configuration.constants.SkillId
import xim.poc.game.configuration.v0.*
import xim.poc.game.configuration.v0.tactics.TacticsStatusHelper.processStatusEffects
import xim.poc.game.configuration.v0.tactics.behaviors.TacticsBehaviorController
import xim.poc.game.configuration.v0.tactics.behaviors.TacticsBehaviorId
import xim.poc.game.configuration.v0.tactics.behaviors.TacticsItemMobBehavior
import xim.poc.game.configuration.v0.tactics.behaviors.TacticsItemMobBehaviorId
import xim.poc.game.event.*
import xim.poc.gl.PortraitBufferManager
import xim.poc.ui.ChatLog
import xim.poc.ui.ChatLogColor
import xim.resource.AoeType
import xim.util.Fps
import xim.util.PI_f
import kotlin.math.abs
import kotlin.math.max
import kotlin.math.roundToInt
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.seconds

class TurnOrderEntry(val source: ActorState, val speculativeTp: Int = source.getTp(), val speculativeTurn: Int)

class TacticsMatchInstance(val config: TacticsMatchConfig) {

    companion object {
        val towerPosition = PlayerPrepPosition
        val towerSummonPositions = listOf(
            PanelPosition(0, 0),
            PanelPosition(1, 0),
            PanelPosition(1, 1),
            PanelPosition(2, 0),
            PanelPosition(2, 1),
            PanelPosition(3, 0),
            PanelPosition(3, 1),
            PanelPosition(4, 0))
    }

    val arenaUi = TacticsUi(this)

    var cursorPosition: CursorPosition = PlayerPrepPosition
        private set

    var matchState: TacticsMatchState = TacticsMatchSetup()
        private set

    private val units = ArrayList<ActorPromise>()
    private val tower: ActorPromise

    private var turnCounter = 0
    private val summonedUnits = ArrayList<UnitId>()

    init {
        tower = makeTower(towerPosition)
        summonInitialEnemies()
        maybeSummonEnemyUnits()
    }

    fun update(elapsedFrames: Float) {
        arenaUi.update(elapsedFrames)

        matchState.update(elapsedFrames)
        if (checkAndSetConclusion()) { return }

        TacticsUnitList.draw(this)
        maybeExecuteAction()

        updateTurn()
        maybePlanEnemyTurn()
    }

    fun cleanUp() {
        arenaUi.cleanUp()
        PortraitBufferManager.releaseClaimedBuffers()
        units.forEach { it.onReady { a -> ActorDeleteEvent(a.id).apply() } }
        tower.onReady { ActorDeleteEvent(it.id).apply() }
    }

    fun getGameSpeed(): Float {
        val turn = matchState
        if (turn !is TacticsMatchUnitTurn || config.isHoldingTurn()) { return 1f }

        val settings = GameV0SaveStateHelper.getState().tacticsState
        val enemySpeed = if (turn.isEnemyTurn()) { settings.enemyTurnSpeed.toFloat() } else { 1f }
        val animationSpeed = if (turn.hasSubmittedAction()) { settings.skillAnimationSpeed.toFloat() } else { 1f }

        return max(enemySpeed, animationSpeed)
    }

    fun getMusic(): BackgroundMusicResponse? {
        return if (matchState is TacticsMatchConcluded) { null } else { config.getMusic(this) ?: BackgroundMusicResponse(146) }
    }

    fun completeSetupPhase() {
        val current = (matchState as? TacticsMatchSetup) ?: return
        current.completed = true
        getPlayerUnits().forEach { (it.behaviorController as? TacticsBehaviorController)?.onMatchStart(this) }
    }

    fun exit() {
        matchState = TacticsMatchConcluded(this, conclusion = MatchConclusion.Exit)
    }

    fun readyToExit(): Boolean {
        val state = (matchState as? TacticsMatchConcluded) ?: return false
        return state.isComplete()
    }

    fun setCursor(cursorPosition: CursorPosition) {
        this.cursorPosition = cursorPosition
    }

    fun adjustCursor(dx: Int, dy: Int) {
        val current = cursorPosition
        cursorPosition = when (current) {
            is PanelPosition -> {
                val newX = (current.position.x + dx).coerceIn(0, 4)
                var newY = current.position.y + dy
                if (arenaUi.context.isBoardOnly()) { newY = newY.coerceIn(0, 4) }
                if (arenaUi.removedRamp) { newY = newY.coerceAtMost(4) }

                if (newY < 0) {
                    PlayerPrepPosition
                } else if (newY > 4) {
                    EnemyPrepPosition(x = if (newX <= 2) { 0 } else { 1 }, y = 0)
                } else {
                    PanelPosition(newX, newY)
                }
            }

            is PlayerPrepPosition -> {
                if (dy == 1) {
                    PanelPosition(2, 0)
                } else {
                    PlayerPrepPosition
                }
            }

            is EnemyPrepPosition -> {
                val newY = (current.position.y + dy).coerceAtMost(4)
                val newX = if (newY == 0) { (current.position.x + dx).coerceIn(0, 1) } else { 0 }

                if (newY == -1) {
                    PanelPosition(2, 4)
                } else {
                    EnemyPrepPosition(newX, newY)
                }
            }
        }

        if (current != cursorPosition) { AudioManager.playSystemSoundEffect(SystemSound.Tick) }
    }

    fun getUnits(): List<ActorState> {
        return units.mapNotNull { it.resolveIfReady() }.filter { !it.isDead() }
    }

    fun getPlayerUnits(): List<ActorState> {
        return getUnits().filter { it.type == ActorType.AllyNpc }
    }

    fun isSetupPhase(): Boolean {
        return matchState is TacticsMatchSetup
    }

    fun getCurrentTurn(): TacticsMatchUnitTurn? {
        return (matchState as? TacticsMatchUnitTurn)
    }

    fun getCurrentTurnUnit(): ActorState? {
        return getCurrentTurn()?.unit
    }

    fun getMovementPositions(unit: ActorState): List<PanelPosition> {
        val moveRange = if (unit.hasStatusEffect(StatusEffect.Bind)) {
            0
        } else {
            val fleeBonus = unit.getStatusEffect(StatusEffect.Flee)?.potency ?: 0
            val weightPenalty = unit.getStatusEffect(StatusEffect.Weight)?.potency ?: 0
            (fleeBonus + unit.getUnit().getMovementRange() - weightPenalty).coerceIn(0, 3)
        }

        val hasPass = unit.getUnit().instincts.any { TacticsInstincts[it.id].enablePass }

        val unitPosition = unit.getCursorPosition()
        if (unitPosition !is PanelPosition) { return emptyList() }

        val toCheck = ArrayDeque<Pair<PanelPosition, Int>>()
        toCheck.addFirst(unitPosition to 0)

        val alreadyChecked = HashSet<PanelPosition>()
        alreadyChecked += unitPosition

        val result = ArrayList<PanelPosition>()

        while (toCheck.isNotEmpty()) {
            val (current, distance) = toCheck.removeFirst()
            alreadyChecked += current

            val occupiedBy = getUnit(current)
            if (!hasPass && occupiedBy != null && occupiedBy.type != unit.type) { continue }
            if (occupiedBy == null || occupiedBy.id == unit.id) { result += current }

            if (distance >= moveRange) { continue }

            val neighbors = neighborsOf(current, range = 1)
                .filter { !alreadyChecked.contains(it) }
                .map { it to distance + 1 }

            toCheck += neighbors
        }

        return result
    }

    fun nearestUnclaimedPosition(base: PanelPosition): PanelPosition? {
        val toCheck = ArrayDeque<PanelPosition>()
        toCheck.addFirst(base)

        val alreadyChecked = HashSet<PanelPosition>()
        alreadyChecked += base

        while (toCheck.isNotEmpty()) {
            val current = toCheck.removeFirst()
            alreadyChecked += current

            getUnit(current) ?: return current
            toCheck += neighborsOf(current, range = 1).filter { !alreadyChecked.contains(it) }.sortedByDescending { it.y }
        }

        return null
    }

    fun getHighlightedUnit(): ActorState? {
        return getUnits().firstOrNull { getUnitPosition(it) == cursorPosition  }
    }

    fun getHighlightedTower(): ActorState? {
        val actor = tower.resolveIfReady() ?: return null
        return if (actor.getCursorPosition() == cursorPosition) { actor } else { null }
    }

    fun getUnit(position: CursorPosition): ActorState? {
        return getUnits().firstOrNull { getUnitPosition(it) == position }
    }

    fun getUnitPosition(unit: ActorState): CursorPosition {
        val actionBuilder = getCurrentTurn()?.actionBuilder

        return if (unit.id == actionBuilder?.actorId) {
            actionBuilder.destination ?: unit.getCursorPosition()
        } else {
            unit.getCursorPosition()
        }
    }

    fun onUnitDefeated(context: ActorDefeatedContext) {
        val defeated = ActorStateManager[context.event.defeated] ?: return
        if (defeated.type != ActorType.Enemy) { return }

        val defeatedBy = ActorStateManager[context.event.defeatedBy] ?: return
        if (defeatedBy.type != ActorType.AllyNpc) { return }

        transferInventory(defeated, context.event.actionContext)
        config.onEnemyDefeated(defeatedBy, defeated, context.event.actionContext)

        val xpMultiplier = defeated.getUnitConfig()?.xpMultiplier ?: 1f
        val unlockedIdleXp = TacticsState.hasUnlockedIdleXp()
        val deployedUnits = getPlayerUnits()

        for (unit in TacticsState.getPlayerUnits()) {
            val deployedUnit = deployedUnits.firstOrNull { it.getUnit().id == unit.id }
            if (deployedUnit == null && !unlockedIdleXp) { continue }

            val result = unit.gainExp(defeated.getUnit(), xpMultiplier)
            deployedUnit?.onGainedXp(result, context.event.actionContext)
        }
    }

    fun onTurnSubmitted() {
        arenaUi.activateContext(TacticsNoopContext)
    }

    private fun checkAndSetConclusion(): Boolean {
        if (matchState is TacticsMatchConcluded) { return true }

        if (isDefeated()) {
            matchState = TacticsMatchConcluded(this, MatchConclusion.Defeat)
            return true
        } else if (isVictory()) {
            matchState = TacticsMatchConcluded(this, MatchConclusion.Victory)
            return true
        }

        return false
    }

    private fun maybeExecuteAction() {
        val turn = getCurrentTurn() ?: return

        if (turn.hasExecutedSkill()) { return }
        val action = turn.getSubmittedAction() ?: return

        val movementCommand = action.source.getMovementCommand()
        if (movementCommand != null && !movementCommand.complete) { return }
        action.source.clearMovementCommand()

        if (!action.source.behaviorController.isRotationLocked()) { action.source.faceToward(action.target) }
        GameEngine.submitEvent(CastMobSkillStart(action.source.id, action.target.id, action.skillId))
    }

    fun neighborsOf(position: PanelPosition, range: Int, allowDiagonal: Boolean = true): List<PanelPosition> {
        val neighbors = ArrayList<PanelPosition>()

        for (y in -range .. range) {
            for (x in -range .. range) {
                if (abs(y) + abs(x) > range) { continue }
                if (!allowDiagonal && y != 0 && x != 0) { continue }
                neighbors += PanelPosition.maybe(position.x + x, position.y + y) ?: continue
            }
        }

        return neighbors
    }

    fun getTargets(source: ActorState, ability: SkillId): List<PanelPosition> {
        val sourcePosition = getUnitPosition(source)
        if (sourcePosition !is PanelPosition) { return emptyList() }

        val result = ArrayList<PanelPosition>()

        val unit = source.getUnit()
        val (minRange, maxRange) = unit.getSkillRange(ability)
        val info = TacticsSkills[ability]

        for (other in getUnits()) {
            val position = getUnitPosition(other)
            if (position !is PanelPosition) { continue }

            val distance = position.distance(sourcePosition)
            if (distance < minRange || distance > maxRange) { continue }

            if (info.aoeType == AoeType.Line && position.isDiagonal(sourcePosition)) { continue }

            val target = ActorStateManager[other.id] ?: continue
            if (!GameEngine.canBeginSkillOnTarget(source, target, ability)) { continue }

            result += position
        }

        return result
    }

    fun getTargets(source: ActorState, positionSkill: TacticsPositionSkill): List<PanelPosition> {
        val sourcePosition = getUnitPosition(source)
        if (sourcePosition !is PanelPosition) { return emptyList() }

        val result = ArrayList<PanelPosition>()

        for (other in getUnits()) {
            val position = getUnitPosition(other)
            if (position !is PanelPosition) { continue }

            val distance = position.distance(sourcePosition)
            if (distance > 1) { continue }

            val target = ActorStateManager[other.id] ?: continue
            if (!positionSkill.isValid(this, source, target)) { continue }

            result += position
        }

        return result
    }

    fun getAbilities(actorState: ActorState): List<SkillId> {
        return (actorState.behaviorController as TacticsBehaviorController).getSkills()
    }

    fun getCurrentTurnCount(): Int {
        return turnCounter
    }

    fun getSummonedUnitIds(): List<UnitId> {
        return summonedUnits
    }

    fun getSummonedUnitCount(): Int {
        return summonedUnits.size
    }

    fun canSummonUnit(unit: TacticsUnit): String? {
        getSummonedUnitPosition() ?: return "Invalid summoning position!"

        if (summonedUnits.contains(unit.id)) {
            return "${unit.name} was already summoned!"
        }

        val summonCount = getSummonedUnitCount()
        if (summonCount >= 4) {
            return "Reached total summon limit!"
        }

        return null
    }

    fun summonPlayerUnit(unit: TacticsUnit): ActorPromise? {
        val failureReason = canSummonUnit(unit)
        if (failureReason != null) {
            ChatLog(failureReason, ChatLogColor.Error)
            return null
        }

        val position = getSummonedUnitPosition() ?: return null

        summonedUnits += unit.id
        return createUnit(position, ActorType.AllyNpc, unit)
    }

    fun releasePlayerUnit(unit: ActorState) {
        summonedUnits.remove(unit.getUnit().id)
        GameEngine.submitDeleteActor(unit.id)
    }

    private fun getSummonedUnitPosition(): PanelPosition? {
        val current = (cursorPosition as? PanelPosition) ?: return null
        if (getUnit(current) != null || !towerSummonPositions.contains(current)) { return null }
        return current
    }

    private fun getTurnOrder(): List<ActorState> {
        return getUnits().sortedWith { a, b ->
            val tpComp = b.getTp().compareTo(a.getTp())
            if (tpComp != 0) { tpComp } else { a.getMostRecentTurn().compareTo(b.getMostRecentTurn()) }
        }
    }

    fun getSpeculativeTurnOrder(): List<TurnOrderEntry> {
        val actionBuilder = getCurrentTurn()?.actionBuilder

        val speculativeTpChangeChecker = getSpeculativeTpChange()
        val currentUnit = getCurrentTurnUnit()

        return getUnits().map {
            val speculativeTurn = if (it.id == currentUnit?.id && actionBuilder?.destination != null) { turnCounter + 1 } else { it.getMostRecentTurn() }
            TurnOrderEntry(it, getSpeculativeTp(it, speculativeTpChangeChecker), speculativeTurn)
        }.sortedWith { a, b ->
            val tpComp = b.speculativeTp.compareTo(a.speculativeTp)
            if (tpComp != 0) { tpComp } else { a.speculativeTurn.compareTo(b.speculativeTurn) }
        }
    }

    private fun getSpeculativeTp(actorState: ActorState, tpChangeChecker: TpChangeChecker?): Int {
        val tp = actorState.getTp()

        val currentTurn = getCurrentTurn() ?: return tp
        if (currentTurn.isEnemyTurn()) { return tp }

        // Executed skill (prevents quick shuffle between submitting & executing)
        val action = currentTurn.getSubmittedAction()
        if (action != null && action.source.id == actorState.id && !currentTurn.hasExecutedSkill()) {
            val cost = getActorSkillCost(actorState, action.skillId)
            return tp - cost.baseCost.value
        }

        val change = (tpChangeChecker?.getTpChange(actorState) ?: 0) - arenaUi.getSelectingCost(actorState)
        return (tp + change).coerceIn(0, GameEngine.getMaxTp(actorState))
    }

    private fun getSpeculativeTpChange(): TpChangeChecker? {
        val currentTurn = getCurrentTurn() ?: return null
        if (currentTurn.hasExecutedSkill()) { return null }

        val actionBuilder = currentTurn.actionBuilder
        val source = ActorStateManager[actionBuilder.actorId] ?: return null
        val target = ActorStateManager[actionBuilder.targetId] ?: getUnit(cursorPosition) ?: return null

        val skillChoice = actionBuilder.skill ?: return null
        val skillId = (skillChoice as? SkillChoice)?.skillId ?: return null

        if (!currentTurn.hasSubmittedAction()) {
            val validTacticsTargets = getTargets(source, skillId)
            if (!validTacticsTargets.contains(target.getCursorPosition())) { return null }
        }

        val logicalTargets = SkillTargetEvaluator.evaluateTargets(skillId, source, target)

        val skillApplier = TacticsSkills[skillId].skillApplier
        val evaluationResult = SkillApplierRunner.applySkill(skillId, skillApplier, source, logicalTargets)

        if (evaluationResult !is ApplierSuccessResult) { return null }

        return TpChangeChecker(evaluationResult)
    }

    private fun updateTurn() {
        val current = matchState

        val currentTurnUnit = getCurrentTurnUnit()
        if (currentTurnUnit?.isEnemy() == true) { cursorPosition = getUnitPosition(currentTurnUnit) } // Camera-lock the enemy

        if (config.isHoldingTurn()) { return }

        if (!current.isComplete()) { return }
        current.onComplete()

        // Bump up TP
        val actors = getUnits()

        val nextTp = (1000 - actors.maxOf { it.getTp() }).coerceAtLeast(0)
        actors.forEach { it.gainTp(nextTp) }
        turnCounter += 1

        // Pick the next actor & advance the match state
        val next = getTurnOrder().first()
        val nextTurn = TacticsMatchUnitTurn(this, next).also { matchState = it }

        // If the match has ended, don't actually begin the next unit's turn
        if (matchState is TacticsMatchConcluded) { return }

        if (!nextTurn.isEnemyTurn()) { arenaUi.reset() }
        cursorPosition = next.getCursorPosition()

        val message = "${next.name}'s turn."
        HelpNotifier.notify(message, 1.seconds)
        ChatLog("${next.name}'s turn.", ChatLogColor.Action)

        processStatusEffects(next)
        maybeSummonEnemyUnits()

        if (next.behaviorController is TacticsBehaviorController) { next.behaviorController.onTurnStart() }
    }

    private fun maybePlanEnemyTurn() {
        val state = getCurrentTurnUnit() ?: return

        val turn = getCurrentTurn() ?: return
        if (!turn.isEnemyTurn() || isDefeated()) { return }

        if (Fps.framesToSeconds(turn.totalTurnTime) < 120.milliseconds) { return }
        if (turn.hasSubmittedAction()) { return }

        if (!state.isIdleOrEngaged()) { return }
        (state.behaviorController as TacticsBehaviorController).submitTurnPlan(this, turn)
    }

    private fun summonInitialEnemies() {
        for (enemy in config.getInitialEnemyUnits()) {
            createUnit(
                unit = enemy.tacticsUnit,
                position = enemy.position,
                type = ActorType.Enemy,
                startingTp = enemy.startingTp,
                unitConfig = enemy.bossConfig,
            )
        }
    }

    private fun maybeSummonEnemyUnits() {
        maybeSummonBossUnit()

        val enemyUnits = getUnits().filter { it.isEnemy() }
        if (enemyUnits.size >= 12) { return }

        var start = 0
        for (i in 0 .. 4) {
            val position = EnemyPrepPosition(0, i)
            getUnit(position) ?: continue
            start = i + 1
        }

        if (start > 4) { return }

        val remainingNames = tacticsUnitNames.toMutableSet()
        enemyUnits.forEach { remainingNames.remove(it.name) }

        for (i in start .. 4) {
            val position = EnemyPrepPosition(0, i)

            val name = remainingNames.random()
            remainingNames.remove(name)

            val (unit, unitConfig) = config.getNextEnemyUnit(listOf(name)) ?: return
            createUnit(position = position, unit = unit, type = ActorType.Enemy, startingTp = 1000, unitConfig = unitConfig)
        }
    }

    private fun maybeSummonBossUnit() {
        if (getUnits().any { it.isBossUnit() }) { return }
        val (bossUnit, bossConfig) = config.getNextBossUnit() ?: return
        val bossPosition = EnemyPrepPosition(1, 0)
        createUnit(position = bossPosition, type = ActorType.Enemy, unit = bossUnit, startingTp = 200, unitConfig = bossConfig)
    }

    private fun makeTower(position: CursorPosition): ActorPromise {
        return GameEngine.submitCreateActorState(InitialActorState(
            name = "",
            modelLook = ModelLook.npc(0xB17),
            position = position.toWorldSpace(),
            rotation = -PI_f/2,
            type = ActorType.Effect,
            movementController = NoOpActorController(),
            behaviorController = NoActionBehaviorId,
            components = listOf(TacticsComponent(position)),
        ))
    }

    fun createUnit(position: CursorPosition, type: ActorType, unit: TacticsUnit, startingTp: Int = 1000, unitConfig: TacticsUnitConfig? = null): ActorPromise {
        val rotation = when (type) {
            ActorType.AllyNpc -> -PI_f/2
            ActorType.Enemy -> PI_f/2
            else -> 0f
        }

        val jobConfig = TacticsJobConfigs[unit.currentJob]

        val unitComponent = TacticsUnitComponent(unit = unit, bossUnitConfig = unitConfig, mostRecentTurnIndex = turnCounter++)
        val additionalScale = unitConfig?.scale ?: 1f

        val behavior = if (unitConfig?.behavior != null) {
            ActorBehaviors.getOrRegister(unitConfig.behavior.id, unitConfig.behavior.provider)
        }  else if (config is TacticsItemMatch) {
            ActorBehaviors.getOrRegister(TacticsItemMobBehaviorId) { TacticsItemMobBehavior(it) }
        } else {
            ActorBehaviors.getOrRegister(TacticsBehaviorId) { TacticsBehaviorController(it) }
        }

        val inventory = Inventory().copyFrom(unitConfig?.inventory ?: Inventory())

        return GameEngine.submitCreateActorState(InitialActorState(
            name = unit.name,
            modelLook = jobConfig.look,
            position = position.toWorldSpace(),
            rotation = rotation,
            type = type,
            movementController = TacticsMovementController(),
            behaviorController = behavior,
            components = listOf(ActorEnmityTable(), TacticsComponent(position), unitComponent, inventory),
            popRoutines = jobConfig.spawnAnimations,
            scale = jobConfig.modelScale * additionalScale,
            appearanceState = jobConfig.defaultAppearanceState,
        )).onReady {
            it.viewCullable = false
            it.animationSettings.deathAnimation = jobConfig.deathAnimation
            it.animationSettings.fadeWait = jobConfig.deathFadeTime ?: 10.seconds
            it.setTp(startingTp)
            PortraitBufferManager.submitPhotoRequest(it.id)
        }.also {
            units += it
        }
    }

    private fun isDefeated(): Boolean {
        val state = matchState

        if (state !is TacticsMatchUnitTurn) { return false }
        if (!state.isComplete()) { return false }

        if (units.any { it.getIfReady() == null }) { return false }
        return getUnits().filter { it.type == ActorType.AllyNpc }.all { it.isDead() }
    }

    private fun isVictory(): Boolean {
        if (!config.isDoneSpawningEnemies() || config.isHoldingTurn()) { return false }

        val state = matchState

        if (state !is TacticsMatchUnitTurn) { return false }
        if (!state.isComplete()) { return false }

        if (units.any { it.getIfReady() == null }) { return false }
        return getUnits().filter { it.type == ActorType.Enemy }.all { it.isDead() }
    }

    private fun transferInventory(source: ActorState, actionContext: AttackContext?) {
        source.getInventory().inventoryItems.map {
            GameEngine.submitEvent(InventoryItemTransferEvent(
                sourceId = source.id,
                destinationId = ActorStateManager.playerId,
                inventoryItemId = it.internalId,
                actionContext = actionContext,
            ))
        }
    }

}

private class TpChangeChecker(val evaluationResult: ApplierSuccessResult) {

    private val skill = TacticsSkills[evaluationResult.executedSkill]

    fun getTpChange(target: ActorState): Int {
        if (!evaluationResult.allTargetIds.contains(target.id)) { return 0 }

        val tpGained = evaluationResult.events.filterIsInstance<ActorAttackedEvent>()
            .filter { it.targetId == target.id }
            .sumOf { it.targetTpGain }

        val skillTpDamage = skill.tpDamage ?: 0

        return tpGained - if (skillTpDamage > 0) {
            val resistance = (target.behaviorController as TacticsBehaviorController).getTpDamageMultiplier()
            (skillTpDamage * resistance).roundToInt()
        } else {
            skillTpDamage
        }
    }

}